import _ from 'lodash';
import React from 'react';
import uuid4 from 'uuid/v4';

import Table from 'components/Table';
import Tooltip from 'components/Tooltip';
import NotesCount from 'components/NotesCount';
import RenewalIcon from 'components/RenewalIcon';
import BooleanIcon from 'components/BooleanIcon';
import BookingStatus from 'components/BookingStatus';
import StatusBox from 'components/StatusBox';
import {BookingTenantsInfo} from 'components/TenantInfo';
import {
  humanReadableGCMandateStatus,
  humanReadableGCInvitationStatus,
  GOCARDLESS_STATUSES_FOR_BOOKING_TRACKER
} from 'ducks/tenancy';
import {BOOKING_VERIFICATION_METHOD} from 'ducks/bookings/detail';
import {BookingStatusesDescriptions} from 'ducks/bookings/list';

import {Paginator, BookingStatusesTooltip} from './components';

import {formatDate} from 'utils/datetime';
import {headOption} from 'utils/common';

import './styles.css';

const TOOLTIP_NAME = 'bookingsTableTooltip';

const RoomInfo = ({room}) => {
  if (_.isNull(room)) {
    return <div>N/A</div>;
  }

  return (
    <div className="roomInfo">
      <div className="number">{room.roomNumber}</div>
      <div className="type">{room.roomType}</div>
    </div>
  );
};

class BookingsTable extends React.Component<any> {
  state = {
    popupPosition: {top: 0, left: 0},
    popupVisible: false
  };

  columnsMap = [
    {className: 'nameColumn', heading: 'Booking', sortField: 'tenant'},
    {className: 'roomColumn', heading: 'Room No'},
    {className: 'notesColumn', heading: 'Notes'},
    {className: 'arrivalColumn', heading: 'Move-in', sortField: 'arrival'},
    {
      className: 'departureColumn',
      heading: 'Move-out',
      sortField: 'departure'
    },
    {className: 'statusColumn', heading: 'Status', sortField: 'status'},
    {className: 'typeColumn', heading: 'Type', sortField: 'type'},
    {className: 'createdColumn', heading: 'Created', sortField: 'createdAt'},
    {
      className: 'createdByColumn',
      heading: 'Created By',
      sortField: 'createdBy'
    },
    {
      className: 'salesAgentColumn',
      heading: 'Sales Agent',
      sortField: 'salesAgent'
    },
    {
      className: 'verificationMethod',
      heading: 'Verification Method'
    },
    {
      className: 'checkedOutEarly',
      heading: 'Checked Out Early'
    },
    {
      className: 'invitationStatus',
      heading: 'GC invite status'
    },
    {
      className: 'mandateStatus',
      heading: 'GC mandate status'
    }
  ];

  showPopup = event => {
    const rect = event.target.getBoundingClientRect();

    // This is just eye-candy
    const tooltipHeight = 520;
    const tooltipWidth = 420;
    let top = rect.top - 50;
    if (top < 60) {
      top = 60;
    }
    if (top > window.innerHeight - (tooltipHeight + 50)) {
      top = window.innerHeight - (tooltipHeight + 50);
    }

    this.setState({
      popupPosition: {top: top, left: rect.left - tooltipWidth},
      popupVisible: true
    });
  };

  closePopup = () => {
    this.setState({popupVisible: false});
  };

  getDescriptionForStatus = bookingStatus => {
    return headOption(
      BookingStatusesDescriptions.filter(
        option => option.label === bookingStatus
      ),
      x => x.description
    );
  };

  render() {
    const {
      bookings,
      pagination,
      sort,
      onPageChange,
      onSort,
      exportBookingsCsvInBackground,
      exportBookingsWithoutFinancialDataCsvInBackground,
      exportContactsCsvInBackground,
      exportingBookingsCsv,
      exportingContactsCsv,
      openBookingNotesModal,
      redirectToBooking
    } = this.props;

    return (
      <div className="BookingsTable">
        <div className="BookingsTable__container">
          <div className="BookingsTable__header">
            <Table>
              <Table.Header>
                {this.columnsMap.map(column => (
                  <Table.HeaderItem
                    key={column.sortField || uuid4()}
                    className={column.className}
                    sortDirection={
                      column.sortField === sort.field ? sort.direction : null
                    }
                    onSort={() => column.sortField && onSort(column.sortField)}
                    heading={column.heading}
                  />
                ))}
              </Table.Header>
            </Table>
          </div>
          <div className="BookingsTable__content">
            <Table>
              <Table.Body>
                {bookings.map(booking => {
                  const invitationStatusColor =
                    GOCARDLESS_STATUSES_FOR_BOOKING_TRACKER[
                      booking.invitationStatus
                    ].color;
                  const mandateStatusColor =
                    GOCARDLESS_STATUSES_FOR_BOOKING_TRACKER[
                      booking.mandateStatus
                    ].color;

                  return (
                    <Table.Row
                      key={booking.id}
                      onClick={() =>
                        redirectToBooking({bookingId: booking.id})
                      }>
                      <Table.Cell className="nameColumn">
                        <div className="colab-centered-v">
                          {booking.primaryTenant && (
                            <BookingTenantsInfo
                              tenants={[
                                booking.primaryTenant,
                                booking.secondaryTenant
                              ].filter(x => !!x)}
                            />
                          )}
                          {booking.isRenewal && <RenewalIcon />}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="roomColumn">
                        <RoomInfo room={booking.room} />
                      </Table.Cell>
                      <Table.Cell className="notesColumn">
                        <NotesCount
                          notesCount={booking.notesCount}
                          showNotification={booking.hasUnresolvedNotes}
                          onClick={() =>
                            openBookingNotesModal({bookingId: booking.id})
                          }
                        />
                      </Table.Cell>
                      <Table.Cell className="arrivalColumn">
                        {formatDate(booking.period.startDate)}
                      </Table.Cell>
                      <Table.Cell className="departureColumn">
                        {formatDate(booking.period.endDate)}
                      </Table.Cell>
                      <Table.Cell
                        className="statusColumn colab-clickable"
                        onClick={event => {
                          event.stopPropagation();
                          return this.showPopup(event);
                        }}>
                        <div
                          data-tip={this.getDescriptionForStatus(
                            booking.status
                          )}
                          data-for={TOOLTIP_NAME}>
                          <BookingStatus status={booking.status} />
                        </div>
                      </Table.Cell>
                      <Table.Cell className="typeColumn">
                        {booking.bookingType}
                      </Table.Cell>
                      <Table.Cell className="createdColumn">
                        {formatDate(booking.createdAt)}
                      </Table.Cell>
                      <Table.Cell className="createdByColumn">
                        {booking.createdBy || booking.source}
                      </Table.Cell>
                      <Table.Cell className="salesAgentColumn">
                        {booking.salesAgent}
                      </Table.Cell>
                      <Table.Cell className="verificationMethod">
                        {_.get(
                          BOOKING_VERIFICATION_METHOD,
                          booking.verificationMethod,
                          booking.verificationMethod
                        )}
                      </Table.Cell>
                      <Table.Cell className="checkedOutEarly">
                        <div className="colab-centered">
                          <BooleanIcon value={booking.isEarlyCheckedOut} />
                        </div>
                      </Table.Cell>
                      <Table.Cell className="invitationStatus">
                        <StatusBox
                          status={humanReadableGCInvitationStatus(
                            booking.invitationStatus
                          )}
                          color={invitationStatusColor}
                        />
                      </Table.Cell>
                      <Table.Cell className="mandateStatus">
                        <StatusBox
                          status={humanReadableGCMandateStatus(
                            booking.mandateStatus
                          )}
                          color={mandateStatusColor}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            <BookingStatusesTooltip
              position={this.state.popupPosition}
              isVisible={this.state.popupVisible}
              onClose={this.closePopup}
            />
            <Tooltip
              place="left"
              delayShow={300}
              effect="solid"
              id={TOOLTIP_NAME}
            />
          </div>
        </div>
        <Paginator
          pagination={pagination}
          onPageChange={onPageChange}
          exportingBookingsCsv={exportingBookingsCsv}
          exportBookingsCsvInBackground={exportBookingsCsvInBackground}
          exportBookingsWithoutFinancialDataCsvInBackground={
            exportBookingsWithoutFinancialDataCsvInBackground
          }
          exportingContactsCsv={exportingContactsCsv}
          exportContactsCsvInBackground={exportContactsCsvInBackground}
          disableExporting={_.isEmpty(bookings)}
        />
      </div>
    );
  }
}

export default BookingsTable;
